import type { ResponsiveStyleValue } from '@mui/system';
import { type MaskOptions, type Track, useMask } from '@react-input/mask';
import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  useId,
  useRef,
} from 'react';
import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';
import { TransitionGroup } from 'react-transition-group';
import InputErrorText from '@/components/InputErrorText/InputErrorText';
import { trimPhone } from '@/utils/phone';
import { maskInputChangeEvent } from '@/utils/utils';
import useInputStyles from './FormInput.styles';

export interface InputProps<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>;
  label?: string;
  setValue?: (value: string) => void;
  error?: string;
  isOnDark?: boolean;
  type?: InputType;
  testId?: string;
  subtext?: string;
  inputSize?: ResponsiveStyleValue<InputSize>;
  register?: UseFormRegister<T>;
  readOnly?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}
export enum InputSize {
  SM = 'sm',
  LG = 'lg',
}

type NativeInputType = React.ComponentProps<'input'>['type'];

export enum InputType {
  TEXT = 'text',
  TEL = 'tel',
  DATE = 'date',
  NUMBER = 'number',

  DOLLAR = 'dollar',
  ZIP = 'zip',
  AUTOCOMPLETE_ADDRESS = 'autocomplete_address',
  // HONEYPOT FIELD
  ADDRESS = 'address',
}

const CUSTOM_INPUT_TYPES = [
  InputType.DOLLAR,
  InputType.ZIP,
  InputType.AUTOCOMPLETE_ADDRESS,
  InputType.ADDRESS,
  InputType.NUMBER,
];

const getNativeInputType = (inputType: InputType): NativeInputType => {
  if (CUSTOM_INPUT_TYPES.includes(inputType)) return InputType.TEXT;

  return inputType;
};

interface InputRowProps {
  children: React.ReactNode;
  classes?: string;
}

/**
 * @deprecated use /components/InputRow
 */
export const InputRow = ({ children, classes }: InputRowProps) => {
  return (
    <div className={classes} style={{ width: '100%', display: 'flex' }}>
      {children}
    </div>
  );
};

export const getMaskByType = (inputType: InputType): MaskOptions => {
  const phoneTrack: Track = ({ inputType, data, selectionStart }) => {
    const inputData = data ?? '';
    if (!inputType.startsWith('insert') || (selectionStart ?? 0) > 1) {
      return inputData;
    }

    const normalizedPhoneDigits = inputData
      .replace(/\D/g, '') // remove all non-digit characters
      .replace(/^1(?=\d{10,}$)/, '') // drop leading US country code "1" when 11+ digits
      .replace(/^[01]/, ''); // remove invalid leading area-code digits (0 or 1)

    return normalizedPhoneDigits;
  };

  const replacement = { _: /\d/ };

  if (inputType === InputType.TEL) {
    return { mask: '(___) ___-____', replacement, track: phoneTrack };
  }
  if (inputType === InputType.DATE) {
    return { mask: '__/__/____', replacement };
  }
  if (inputType === InputType.ZIP) {
    return { mask: '_____', replacement };
  }
  return {};
};

const FormInput = <T extends FieldValues>({
  name,
  label,
  setValue,
  error,
  isOnDark = false,
  type = InputType.TEXT,
  onBlur,
  onChange,
  testId,
  placeholder,
  subtext,
  register,
  inputSize = InputSize.LG,
  readOnly,
  inputRef,
  ...rest
}: InputProps<T>) => {
  const { classes, cx } = useInputStyles({ isOnDark, inputSize });
  const inputName = `${name}-${useId()}`;

  const getRegister = (
    register: UseFormRegister<T> | undefined,
    type: InputType,
    name: Path<T>
  ) => {
    if (!register) {
      return null;
    }

    if (type === InputType.DOLLAR) {
      return register(name, {
        setValueAs: (v: string) => v.replace('$', ''),
      });
    }

    if (type === InputType.TEL) {
      return register(name, {
        setValueAs: (v: string) => trimPhone(v),
      });
    }

    return register(name);
  };
  const registerValues = getRegister(register, type, name);
  const {
    // We have custom implementations of these props so want to remove them from register
    ref: rhfRef,
    onChange: rhfOnChange,
    onBlur: rhfOnBlur,
    ...rhfPropsToSpread
  } = registerValues || {};

  const maskRef = useMask(getMaskByType(type));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const maskedEvent = maskInputChangeEvent(event, type);
    if (onChange) onChange(maskedEvent);

    if (setValue) {
      setValue(maskedEvent.target.value);
    }
    if (rhfOnChange) {
      rhfOnChange(maskedEvent);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(event);
    if (rhfOnBlur) rhfOnBlur(event);
  };

  const combinedRef = (element: HTMLInputElement) => {
    if (rhfRef) {
      rhfRef(element);
    }

    if (inputRef) {
      if (typeof inputRef === 'function') {
        inputRef(element);
      } else {
        inputRef.current = element;
      }
    }

    if (maskedInputTypes.includes(type)) {
      maskRef.current = element;
    }
  };

  const maskedInputTypes: InputType[] = [
    InputType.TEL,
    InputType.DATE,
    InputType.ZIP,
  ];

  const hasError = !!error && error.length > 0;
  const errorId = `${name}error`;
  const errorMessageRef = useRef<HTMLSpanElement>(null);

  const inputNativeType = getNativeInputType(type);

  const inputClasses = cx(classes.input, classes.responsiveSizes, {
    [classes.inputFocusActivity]: !hasError && !readOnly,
    [classes.inputHoverActivity]: !hasError && !readOnly,
    [classes.inputError]: hasError,
  });

  const inputProps = {
    // For RHF, this name will be overwritten
    name: inputName,
    ...rest,
    ...rhfPropsToSpread,
    onBlur: handleBlur,
    onChange: handleChange,
    type: inputNativeType,
    ref: combinedRef,
    placeholder,
    id: inputName,
    className: inputClasses,
    ...(readOnly && { readOnly: true, 'aria-readonly': true }),
    'aria-label': label || name,
    'data-testid': testId,
    'aria-invalid': hasError ? ('true' as const) : ('false' as const),
    'aria-errormessage': errorId,
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.inputContainer}>
        <label htmlFor={inputName} className={classes.inputLabel}>
          {label}
        </label>
        <input {...inputProps} />
      </div>
      {(hasError || subtext) && (
        <div className={classes.bottomTextContainer}>
          <TransitionGroup>
            {hasError ? (
              <InputErrorText
                errorId={errorId}
                errorMessageRef={errorMessageRef}
                errorMessage={error}
                isOnDark={isOnDark}
              />
            ) : (
              subtext && (
                // biome-ignore lint/a11y/noLabelWithoutControl: Automated migration to Biome
                <label className={cx(classes.helperText, classes.subtext)}>
                  {subtext}
                </label>
              )
            )}
          </TransitionGroup>
        </div>
      )}
    </div>
  );
};

export default FormInput;
